import React, { useEffect, useState } from 'react';
import Bread from 'modules/layoutModule/components/Bread/Bread';
import { HomeOutlined, EditOutlined, DeleteFilled } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import {
  Breadcrumb,
  Pagination,
  Popconfirm,
  ConfigProvider,
  BackTop,
} from 'antd';
import { Table, Space, Tooltip, Button } from 'antd';
import TableDateRow from 'modules/layoutModule/components/TableDateRow/TableDateRow';
import plPL from 'antd/lib/locale/pl_PL';
import moment from 'moment';
import TableDataRowTypes from 'modules/layoutModule/enums/TableDataRowTypes';
import useColumnSearch from 'modules/layoutModule/hooks/useColumnSearch';
import {
  getRepresentativesRequest,
  removeSpecificRepresentativesRequest,
} from '../requests/contactRequests';

export interface NewsTableTypes {
  id: number;
  dateAdd: moment.MomentInput;
  name: string;
  isActive: number;
  deleteDisabled?: boolean;
}

const Representatives = (): JSX.Element => {
  const [tableData, setTableData] = useState<NewsTableTypes[]>([]);
  const [loading, toggleLoading] = useState<boolean>(true);
  const [searchedColumn, setSearchedColumn] = useState('');

  const confirmDelete = (id: number) => {
    removeSpecificRepresentativesRequest(id).then(() => {
      downloadRepresentatives();
    });
  };

  const downloadRepresentatives = () =>
    getRepresentativesRequest().then((response) => {
      if (response) {
        const { data } = response;
        setTableData(data);
        toggleLoading(false);
      } else {
        toggleLoading(false);
      }
    });

  useEffect(() => {
    if (tableData.length === 0) {
      downloadRepresentatives();
    }
  }, [tableData]);

  const columns = [
    {
      title: 'Imię i nazwisko',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: { name: string }, b: { name: string }) =>
        a.name.localeCompare(b.name),
      ...useColumnSearch(
        'name',
        'Szukaj po imieniu i nazwisku',
        'Szukaj',
        'Resetuj',
        searchedColumn,
        setSearchedColumn
      ),
    },
    {
      title: 'Avatar',
      dataIndex: 'fileImage1',
      key: 'fileImage1',
      width: 100,
      render: (text: string, record: any) => (
        <TableDateRow record={record} field={TableDataRowTypes.AVATAR} />
      ),
    },
    {
      title: 'Numer telefonu',
      dataIndex: 'phone',
      key: 'phone',
      width: 150,
    },
    {
      title: 'E-mail',
      dataIndex: 'email',
      key: 'email',
      width: 350,
    },
    {
      title: 'Kolejność',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 100,
      sorter: (a: any, b: any) => a.sortOrder - b.sortOrder,
      render: (text: string, record: any) => (
        <TableDateRow record={record} field={TableDataRowTypes.SORT} />
      ),
    },
    {
      title: 'PT',
      dataIndex: 'isActive',
      key: 'isActive',
      width: '100px',
      render: (text: string, record: any) => (
        <TableDateRow record={record} field={TableDataRowTypes.IS_ACTIVE} />
      ),
      sorter: (a: { isActive: number }, b: { isActive: number }) =>
        a.isActive - b.isActive,
      filters: [
        {
          text: 'TAK',
          value: 'isActive',
        },
        {
          text: 'NIE',
          value: 'isNotActive',
        },
      ],
      onFilter: (value: string, record: { isActive: boolean }) => {
        if (value === 'isActive') {
          return record.isActive;
        }
        return !record.isActive;
      },
    },
    {
      title: 'WT',
      dataIndex: 'isNews',
      key: 'isNews',
      width: '100px',
      render: (text: string, record: any) => (
        <TableDateRow record={record} field={TableDataRowTypes.IS_NEWS} />
      ),
      sorter: (a: { isNews: number }, b: { isNews: number }) =>
        a.isNews - b.isNews,
      filters: [
        {
          text: 'TAK',
          value: 'isNews',
        },
        {
          text: 'NIE',
          value: 'isNotActive',
        },
      ],
      onFilter: (value: string, record: { isNews: boolean }) => {
        if (value === 'isNews') {
          return record.isNews;
        }
        return !record.isNews;
      },
    },
    {
      title: 'Akcje',
      dataIndex: 'operation',
      key: 'operation',
      width: 100,
      render: (text: string, item: NewsTableTypes) => {
        return (
          <Space
            size="middle"
            style={{ display: 'Flex', flexDirection: 'column' }}
          >
            <Tooltip title="Edytuj wpis">
              <Link to={`/representatives/${item.id}`}>
                <Button type="primary" shape="circle" icon={<EditOutlined />} />
              </Link>
            </Tooltip>
            <Tooltip title="Usuń wpis">
              <Popconfirm
                title="Czy na pewno chcesz usunąc ten wpis?"
                placement="left"
                onConfirm={() => confirmDelete(item.id)}
                okText="Tak"
                cancelText="Nie"
              >
                <Button
                  type="link"
                  disabled={item.isActive === 1 || item.deleteDisabled}
                >
                  <DeleteFilled />
                </Button>
              </Popconfirm>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Bread>
        <>
          <Breadcrumb.Item>
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>Kontakt</Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/representatives">Przedstawiciele terenowi</Link>
          </Breadcrumb.Item>
        </>
      </Bread>
      <ConfigProvider locale={plPL}>
        <Table
          // @ts-ignore
          columns={columns}
          // @ts-ignore
          dataSource={tableData}
          loading={loading}
          rowKey={(record) => record.id}
        >
          <Pagination />
        </Table>
        <BackTop />
      </ConfigProvider>
    </>
  );
};

export default Representatives;
